import * as React from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/context/ToastContext';
import { FileUploadArea } from './FileUploadArea';
import { WebScraperCard } from './WebScraperCard';
import { VoiceCloneCard } from './VoiceCloneCard';
import { FileDisplayList } from './FileDisplayList';
import { FileListItem } from './FileListItem';
import { LinkInputSection } from './LinkInputSection';
import { LinkDisplayList } from './LinkDisplayList';
import { CollectInfoModal } from './modals/CollectInfoModal';
import { TrainingConfirmModal } from './modals/TrainingConfirmModal';
import { SearchWarningModal } from './modals/SearchWarningModal';
import { VoiceCloneModal } from './modals/voice-clone/VoiceCloneModal';
import { TrainingProgressView } from './TrainingProgressView';
import { useTraining } from '../../hooks/useTraining';
import { useVoiceClone } from '../../hooks/useVoiceClone';
import { useAuth } from '@/hooks/useAuth';
import type { TrainingSectionProps } from '../../types';

export const TrainingSection: React.FC<TrainingSectionProps> = ({
  draftId,
  draftStatus,
  isRetrainMode,
  onBack,
  onTrainingComplete,
  onSaveForLater,
}) => {
  const [isCollectModalOpen, setIsCollectModalOpen] = React.useState(false);
  const [isPremiumCollectModalOpen, setIsPremiumCollectModalOpen] = React.useState(false);
  const [freeCollectEnabled, setFreeCollectEnabled] = React.useState(false);
  const [premiumCollectEnabled, setPremiumCollectEnabled] = React.useState(false);
  const [isVoiceModalOpen, setIsVoiceModalOpen] = React.useState(false);
  const [showSearchWarning, setShowSearchWarning] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [trainingStarted, setTrainingStarted] = React.useState(false);
  const { addToast } = useToast();

  const showToast = React.useCallback(
    (message: string) => {
      addToast({ title: message, subtitle: '', type: 'success' });
    },
    [addToast]
  );

  const { user } = useAuth();

  const {
    files,
    setFiles,
    links,
    isProcessing,
    isLoadingData,
    addFile,
    removeFile,
    retryUpload,
    toggleScreenshotFlag,
    addLink,
    removeLink,
    trainingSteps,
    trainingDone,
    trainingError,
    trainEchoStream,
    preloadedConfig,
    reconnectToTraining,
    scrapingQualityReport,
    retrainBenchmark,
    retrainEcho,
    idleVideoStatus,
  } = useTraining(draftId);

  const voiceClone = useVoiceClone({ draftId, trainingDone, setFiles });

  const trainingFiles = React.useMemo(
    () => files.filter((file) => !file.id.startsWith('voice-clone-')),
    [files]
  );
  const voiceCloneFiles = React.useMemo(
    () => files.filter((file) => file.id.startsWith('voice-clone-')),
    [files]
  );

  // Auto-reconnect if page loaded while training was in progress
  const hasReconnected = React.useRef(false);
  React.useEffect(() => {
    if (draftStatus === 'training_in_progress' && !hasReconnected.current) {
      hasReconnected.current = true;
      setTrainingStarted(true);
      reconnectToTraining();
    }
    return () => {
      hasReconnected.current = false;
    };
  }, [draftStatus, reconnectToTraining]);

  const collectTier = premiumCollectEnabled ? ('premium' as const) : ('free' as const);

  const handleStartTraining = () => {
    if (isRetrainMode) {
      if (!freeCollectEnabled && !premiumCollectEnabled) {
        setShowSearchWarning(true);
        return;
      }
      voiceClone.resetState();
      setTrainingStarted(true);
      retrainEcho(draftId, collectTier);
      return;
    }
    if (!freeCollectEnabled && !premiumCollectEnabled) {
      setShowSearchWarning(true);
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmTraining = () => {
    setShowConfirmModal(false);
    voiceClone.resetState();
    setTrainingStarted(true);
    trainEchoStream(collectTier);
  };

  const handleRetry = () => {
    voiceClone.resetState();
    if (isRetrainMode) {
      retrainEcho(draftId, collectTier);
    } else {
      trainEchoStream(collectTier);
    }
  };

  const handleFreeCollectActivate = () => {
    setFreeCollectEnabled(true);
    setPremiumCollectEnabled(false);
    setIsCollectModalOpen(false);
    showToast('Free internet search activated');
  };

  const handlePremiumCollectActivate = () => {
    setPremiumCollectEnabled(true);
    setFreeCollectEnabled(false);
    setIsPremiumCollectModalOpen(false);
    showToast('Premium internet search activated (55 credits)');
  };

  // ── Training Progress View ──

  if (trainingStarted) {
    return (
      <>
        <TrainingProgressView
          trainingSteps={trainingSteps}
          trainingDone={trainingDone}
          trainingError={trainingError}
          isFullyDone={voiceClone.isFullyDone}
          voiceClone={{
            pendingFile: voiceClone.pendingVoiceFile,
            status: voiceClone.status,
            uploadError: voiceClone.uploadError,
            hasError: voiceClone.hasError,
          }}
          preloadedConfig={preloadedConfig}
          scrapingQualityReport={scrapingQualityReport}
          retrainBenchmark={retrainBenchmark}
          idleVideoStatus={idleVideoStatus}
          onTrainingComplete={onTrainingComplete}
          onRetry={handleRetry}
          onRetryVoiceClone={() => setIsVoiceModalOpen(true)}
          onBackFromProgress={() => setTrainingStarted(false)}
        />
        <VoiceCloneModal
          isOpen={isVoiceModalOpen}
          onClose={() => setIsVoiceModalOpen(false)}
          onFileSelect={voiceClone.handleFileSelect}
        />
      </>
    );
  }

  // ── Upload / Configuration View ──

  const isDisabled = isProcessing || isLoadingData;

  return (
    <div className='animate-slide-in w-full'>
      {/* Header with Back Button */}
      <div className='mb-2 flex items-center gap-3'>
        <button
          onClick={onBack}
          disabled={isDisabled}
          className='rounded-full p-1 transition-colors hover:bg-gray-100 disabled:opacity-50'
          aria-label='Go back'
        >
          <ArrowLeftIcon className='size-6 text-dark-100' strokeWidth={2.5} />
        </button>
        <h2 className='font-proxima text-24 font-extrabold uppercase leading-47 text-brand-800 md:text-40'>
          TRAINING YOUR ECHO
        </h2>
      </div>
      <p className='mb-8 ml-10 font-sans text-16 font-medium leading-27 text-gray-530'>
        Upload content to train your digital echo.
      </p>

      <div>
        <div className='mb-10 flex flex-col items-center gap-8 xl:flex-row xl:items-start'>
          <div className='w-full shrink-0 xl:w-auto'>
            <FileUploadArea onFileSelect={addFile} />
          </div>

          <div className='flex items-center justify-center xl:self-center'>
            <span className='font-sans text-16 font-bold text-dark-100'>OR</span>
          </div>

          <div className='flex w-full flex-wrap gap-4 xl:w-auto'>
            <WebScraperCard
              onStart={() => setIsCollectModalOpen(true)}
              variant='free'
              isActivated={freeCollectEnabled}
            />
            <WebScraperCard
              onStart={() => setIsPremiumCollectModalOpen(true)}
              variant='premium'
              isActivated={premiumCollectEnabled}
            />
            <VoiceCloneCard onClick={() => setIsVoiceModalOpen(true)} />
          </div>
        </div>

        <FileDisplayList
          files={trainingFiles}
          onCancel={removeFile}
          onRetry={retryUpload}
          onDelete={removeFile}
          onToggleScreenshots={toggleScreenshotFlag}
        />

        {/* Voice clone file — separate section */}
        {voiceCloneFiles.length > 0 && (
          <div className='mb-8 w-full'>
            <h4 className='mb-4 font-sans text-20 font-medium leading-27 text-dark-100'>
              Your Cloned Voice
            </h4>
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
              {voiceCloneFiles.map((file) => (
                <FileListItem
                  key={file.id}
                  file={file}
                  onCancel={removeFile}
                  onRetry={retryUpload}
                  onDelete={removeFile}
                />
              ))}
            </div>
          </div>
        )}

        <LinkInputSection onAddLink={addLink} />
        <LinkDisplayList links={links} onDelete={removeLink} />
      </div>

      <div className='mt-8 flex flex-col items-center justify-between gap-6 md:flex-row'>
        <div className='w-full md:w-auto'>
          <Button
            variant='purple-dark'
            onClick={handleStartTraining}
            disabled={isDisabled}
            className='h-[44px] w-full text-16 font-bold uppercase tracking-wide md:w-[220px]'
          >
            {isRetrainMode ? 'RETRAIN ECHO' : 'START TRAINING'}
          </Button>
        </div>

        <button
          onClick={onSaveForLater}
          disabled={isDisabled}
          className='font-sans text-16 font-semibold text-gray-650 underline decoration-solid transition-colors hover:text-brand-800 disabled:opacity-50'
        >
          Save for Later
        </button>
      </div>

      <CollectInfoModal
        isOpen={isCollectModalOpen}
        onClose={() => setIsCollectModalOpen(false)}
        onConfirm={handleFreeCollectActivate}
        variant='free'
      />

      <CollectInfoModal
        isOpen={isPremiumCollectModalOpen}
        onClose={() => setIsPremiumCollectModalOpen(false)}
        onConfirm={handlePremiumCollectActivate}
        variant='premium'
      />

      <VoiceCloneModal
        isOpen={isVoiceModalOpen}
        onClose={() => setIsVoiceModalOpen(false)}
        onFileSelect={voiceClone.handleFileSelect}
      />

      <TrainingConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmTraining}
        hasVoiceClone={voiceClone.pendingVoiceFile !== null}
        hasPremiumScraping={premiumCollectEnabled}
        hasFreeScraping={freeCollectEnabled}
        fileCount={trainingFiles.length}
        linkCount={links.length}
        userCredits={user?.credits ?? 0}
      />

      <SearchWarningModal isOpen={showSearchWarning} onClose={() => setShowSearchWarning(false)} />
    </div>
  );
};
